import React from 'react';
import {
  Box, Button, Container, IconButton, Stack, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { IconSparkles, IconX } from '@marageti/z4-lib';
import { useTravelerProfileSuggestionsStore } from '../../store';

/**
 * Full-width banner shown above the profile content when AI-extracted
 * traveler info is available to import. Replaces the previous entry modal.
 *
 * Self-contained: reads `canEnter` / `isEntryAlertOpen` / actions from the
 * suggestions context and only renders when the gate is open. Visibility is
 * already restricted to ADMIN/CARE via `canEnter` (which requires `canEdit`).
 *
 * Colors are sourced from `theme.palette.ai` so this banner and the
 * AiGeneratedChip share a single source of truth for the AI provenance
 * signal.
 *
 * Layout:
 *  - Desktop: single 56px tall row, with Review & Import button overlapping
 *    the centered text (matches Figma).
 *  - Mobile: message + dismiss on top row, Review & Import button below
 *    indented to align with the message text (not the sparkle icon).
 */
const AiEntryAlert = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const ctx = useTravelerProfileSuggestionsStore();
  if (!ctx) return null;

  const {
    canEnter, isEntryAlertOpen, startReview, skipEntryForSession,
  } = ctx;
  if (!canEnter || !isEntryAlertOpen) return null;

  return (
    <Box
      // Polite live region: the banner is a passive offer, not an interrupt.
      // `role="alert"` would force assistive tech to interrupt the user every
      // time a gated-empty profile loads, which is the wrong UX for this
      // notification.
      role="status"
      sx={{
        bgcolor: 'ai.background',
        color: 'ai.text',
        py: { xs: 1.5, md: 0 },
        minHeight: { md: 56 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Container maxWidth="xl" sx={{ width: '100%' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, md: 2 }}
        >
          <Stack
            direction="row"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1}
            sx={{ minWidth: 0, flex: 1 }}
          >
            <IconSparkles
              sx={{
                fontSize: 20,
                color: 'ai.text',
                flexShrink: 0,
                mt: { xs: '2px', md: 0 },
              }}
            />
            <Typography variant="bodyBold" sx={{ color: 'ai.text', flex: 1 }}>
              We found traveler information in notes, conversations, and reviews that can be imported to this profile.
            </Typography>
            <IconButton
              size="small"
              aria-label="Dismiss AI import suggestion"
              aria-hidden={!isMobile}
              onClick={skipEntryForSession}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                mt: '-4px',
                mr: '-4px',
              }}
            >
              <IconX sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexShrink: 0,
              alignSelf: { xs: 'flex-start', md: 'auto' },
              // Indent under the text block on mobile so the button lines up
              // with the message rather than the sparkle icon. 20px icon + 8px
              // spacing (Stack spacing={1}) on the parent row.
              pl: { xs: 'calc(20px + 8px)', md: 0 },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={startReview}
            >
              Review & Import
            </Button>
            <IconButton
              size="small"
              aria-label="Dismiss AI import suggestion"
              aria-hidden={isMobile}
              onClick={skipEntryForSession}
              sx={{
                color: 'ai.text',
                display: { xs: 'none', md: 'inline-flex' },
              }}
            >
              <IconX sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default AiEntryAlert;
